import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, ShoppingCart, Lock, ChevronLeft as SliderLeft, ChevronRight as SliderRight } from 'lucide-react';
import { catalogAPI, specialsAPI, siteAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import ProductCard from '../ProductCard';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

// Weekly Specials fixed promotional images (ordered 1-4)
const WEEKLY_SPECIALS_IMAGES = [
  'https://customer-assets.emergentagent.com/job_petals-west-b2b/artifacts/e0strksr_petals-west-1.webp',
  'https://customer-assets.emergentagent.com/job_petals-west-b2b/artifacts/7qy84efx_petals-west-2.webp',
  'https://customer-assets.emergentagent.com/job_petals-west-b2b/artifacts/cjo52i54_petals-west-3.webp',
  'https://customer-assets.emergentagent.com/job_petals-west-b2b/artifacts/w267tvnl_petals-west-4.webp',
];

export default function HomePage() {
  const [specials, setSpecials] = useState({ products: [], available: true, message: '' });
  const [categories, setCategories] = useState([]);
  const [cms, setCms] = useState(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, cmsRes] = await Promise.all([
        catalogAPI.getCategories(),
        siteAPI.getSettings()
      ]);
      setCategories(catRes.data.categories);
      const cmsData = cmsRes.data;
      setCms(cmsData);

      // Load weekly specials - use CMS product_ids if set, otherwise default
      const selectedIds = cmsData?.weekly_specials?.product_ids || [];
      const specRes = selectedIds.length > 0
        ? await specialsAPI.getWeeklySpecials({ ids: selectedIds.join(',') })
        : await specialsAPI.getWeeklySpecials();
      setSpecials(specRes.data);
    } catch (err) {
      console.error('Failed to load home data', err);
    }
  };

  const handleQuickAdd = async (productId) => {
    if (!user) { navigate('/login'); return; }
    try {
      await addToCart(productId, 1);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add'));
    }
  };

  // CMS data with fallbacks
  const hero = cms?.hero || { slides: [{ image_url: '', subtitle: 'VALENTINE SPECIAL FLOWERS', title: 'Love is in the air', button1_text: 'Shop Now', button1_link: '/shopnow', button2_text: 'Prebook Orders', button2_link: '/prebook' }] };
  const ws = cms?.weekly_specials || { title: 'Weekly Specials', link_text: 'Book Specials Now', link_url: '/specials' };
  const pb = cms?.prebook_banner || { title: 'Prebook Orders', subtitle: '', button_text: 'Prebook Orders', button_link: '/shop?prebook=true', image_url: '' };
  const sbc = cms?.shop_by_category || { title: 'Shop by Category', categories: [] };
  const about = cms?.about_us || { title: 'ABOUT US', subtitle: 'Since 1960', text: '', image_url: '' };
  const ig = cms?.instagram || { title: 'FIND US\nON INSTAGRAM', text: 'Stay updated on the latest\ntrends and releases.', image_url: '' };

  const slides = hero.slides || [];
  const currentSlide = slides[heroIndex] || slides[0] || {};

  const nextSlide = () => setHeroIndex(i => (i + 1) % slides.length);
  const prevSlide = () => setHeroIndex(i => (i - 1 + slides.length) % slides.length);

  return (
    <div>
      {/* Hero Section with Slider */}
      <section data-testid="home-hero" className="relative w-full h-[400px] md:h-[520px] overflow-hidden">
        <img src={currentSlide.image_url} alt={currentSlide.title || 'Hero'} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-4">
          {currentSlide.subtitle && <p className="text-xs md:text-sm tracking-[0.3em] uppercase mb-3">{currentSlide.subtitle}</p>}
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 400, fontStyle: 'normal' }} className="text-4xl md:text-6xl lg:text-7xl tracking-wide mb-6">{currentSlide.title}</h1>
          <div className="flex flex-col sm:flex-row gap-3">
            {currentSlide.button1_text && (
              <Link data-testid="home-hero-shop-now-button" to={currentSlide.button1_link || '/shop'} className="bg-white text-[#2d2d2d] px-8 py-3 text-xs tracking-[0.18em] uppercase rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                {currentSlide.button1_text}
              </Link>
            )}
            {currentSlide.button2_text && (
              <Link data-testid="home-hero-prebook-orders-button" to={currentSlide.button2_link || '/shop?prebook=true'} className="bg-white text-[#2d2d2d] px-8 py-3 text-xs tracking-[0.18em] uppercase rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                {currentSlide.button2_text}
              </Link>
            )}
          </div>
        </div>
        {/* Slider controls */}
        {slides.length > 1 && (
          <>
            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors backdrop-blur-sm">
              <SliderLeft size={20} />
            </button>
            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors backdrop-blur-sm">
              <SliderRight size={20} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setHeroIndex(i)} className={`w-2 h-2 rounded-full transition-colors ${i === heroIndex ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Weekly Specials */}
      <section data-testid="home-weekly-specials" className="py-10 md:py-14">
        {/* Title row with container */}
        <div className="pw-container">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base md:text-lg font-semibold tracking-[0.15em] uppercase">{ws.title}</h2>
            <Link data-testid="home-weekly-specials-book-link" to={ws.link_url || '/shop?weekly_special=true'} className="flex items-center gap-1 text-xs tracking-wider uppercase text-[#2f3e36] hover:text-[#2d2d2d] transition-colors font-medium underline underline-offset-4 decoration-[#2f3e36]">
              {ws.link_text || 'Book Specials Now'} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        {!specials.available && (
          <div className="pw-container">
            <div className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-xl p-6 text-center mb-6">
              <p className="text-[#6f6a63]">{specials.message}</p>
            </div>
          </div>
        )}
        {/* Full-width images grid - photos only, click navigates to specials or login */}
        <div className="grid grid-cols-2 md:grid-cols-4">
          {specials.products.slice(0, 4).map((product, index) => (
            <Link
              key={product._id}
              to={user ? '/specials' : '/login'}
              data-testid={`home-weekly-special-img-${index}`}
              className="group relative aspect-square overflow-hidden block"
            >
              <img
                src={product.images?.[0] || WEEKLY_SPECIALS_IMAGES[index % 4]}
                alt="Weekly Special"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
          ))}
        </div>
      </section>

      {/* Prebook Banner */}
      <section data-testid="home-prebook-banner" className="relative w-full h-[200px] md:h-[280px] overflow-hidden">
        <img src={pb.image_url} alt={pb.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative h-full flex flex-col items-start justify-center pw-container">
          <h2 className="text-xl md:text-3xl font-semibold uppercase tracking-wider mb-2 text-[#2f3e36]">{(pb.title || '').split('\n').map((line, i) => <React.Fragment key={i}>{line}<br /></React.Fragment>)}</h2>
          {pb.subtitle && <p className="text-sm text-[#4a4a4a] mb-2">{pb.subtitle}</p>}
          <Link
            data-testid="home-prebook-banner-cta"
            to={pb.button_link || '/shop?prebook=true'}
            className="mt-4 bg-[#2f3e36] text-white px-6 py-2.5 text-xs tracking-[0.18em] rounded-lg hover:bg-[#253029] transition-colors"
          >
            {pb.button_text || 'Prebook Orders'}
          </Link>
        </div>
      </section>

      {/* Shop by Category */}
      <section data-testid="home-shop-by-category" className="pt-10 md:pt-14">
        {/* Title row */}
        <div className="pw-container">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base md:text-lg font-semibold tracking-[0.15em] uppercase">{sbc.title}</h2>
            <Link to="/shop" className="flex items-center gap-1 text-xs tracking-wider text-[#2d2d2d] hover:text-[#2f6b4f] transition-colors font-medium underline underline-offset-4">
              View More <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        {/* 2x2 Grid - full width, no gaps */}
        <div className="grid grid-cols-2">
          {(sbc.categories || []).map((cat, i) => (
            <Link
              key={i}
              to={cat.link || '/shop'}
              className="group relative aspect-square overflow-hidden"
            >
              <img
                src={cat.image_url}
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <span className={`absolute top-4 left-4 text-xs tracking-[0.18em] uppercase ${i === 3 ? 'text-[#e0e0e0]' : 'text-[#4a4a4a]'}`}>{cat.name}</span>
              <span className={`absolute bottom-4 right-4 flex items-center gap-1 text-xs tracking-wider ${i === 3 ? 'text-[#e0e0e0] group-hover:text-white' : 'text-[#4a4a4a] group-hover:text-[#2d2d2d]'} transition-colors`}>
                View More <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* About Us */}
      <section data-testid="home-about" className="grid md:grid-cols-2">
        {/* Left - Text */}
        <div className="flex flex-col justify-between px-6 md:px-12 lg:px-16 py-10 md:py-14">
          <div>
            <h2 className="text-lg md:text-xl font-bold tracking-[0.15em] uppercase mb-1">{about.title}</h2>
            {about.subtitle && <p data-testid="home-about-since-1960" className="text-sm font-bold">{about.subtitle}</p>}
          </div>
          <div className="mt-auto pt-16 md:pt-24">
            <p className="text-sm leading-7 text-[#4a4a4a]">
              {(about.text || '').split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < (about.text || '').split('\n').length - 1 && <br />}</React.Fragment>)}
            </p>
          </div>
        </div>
        {/* Right - Image */}
        <div className="aspect-square md:aspect-auto">
          <img src={about.image_url} alt={about.title} className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Instagram */}
      <section data-testid="home-instagram" className="grid md:grid-cols-5">
        {/* Left - Text (2 cols) */}
        <div className="md:col-span-2 flex flex-col justify-between px-6 md:px-12 lg:px-16 py-10 md:py-14">
          <div>
            <h2 className="text-lg md:text-xl font-bold tracking-[0.12em] uppercase leading-tight">
              {(ig.title || '').split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < (ig.title || '').split('\n').length - 1 && <br />}</React.Fragment>)}
            </h2>
          </div>
          <div className="mt-auto pt-16 md:pt-24">
            <p className="text-sm text-[#6f6a63]">
              {(ig.text || '').split('\n').map((line, i) => <React.Fragment key={i}>{line}{i < (ig.text || '').split('\n').length - 1 && <br />}</React.Fragment>)}
            </p>
          </div>
        </div>
        {/* Right - Single image with padding */}
        <div className="md:col-span-3" style={{ paddingTop: '40px', paddingRight: '40px' }}>
          <div className="w-full h-full overflow-hidden">
            <img src={ig.image_url} alt="Instagram" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
